import type { EntityType } from 'lib/entities';
import type {
  CleanupActionOutcome,
  CleanupActionSource,
  CleanupLogRow,
  CleanupRole,
} from './types';

// Field names as the clean-up report spec words them, shared by the table
// headers and the detail so a field reads the same in both.
export const FIELD_LABEL: Partial<Record<keyof CleanupLogRow, string>> = {
  actionAt: 'Action Timestamp',
  actor: 'Actor',
  actorType: 'Actor Type',
  appContext: 'App Context',
  actionOutcome: 'Action Outcome',
  actionSource: 'Action Source',
  source: 'Activity Source',
  entityName: 'Entity Name',
  entityId: 'Entity ID',
  entityType: 'Entity Type',
  entityCreatedAt: 'Entity Created At',
  survivorName: 'Surviving Entity Name',
  survivorId: 'Surviving Entity ID',
  survivorType: 'Surviving Entity Type',
  role: 'Role in Action',
  county: 'County',
  autoMatched: 'Auto-Matched',
  confidence: 'Confidence Score',
  confidenceFactors: 'Scoring Details',
  reversed: 'Reversed',
  undoneAt: 'Undone At',
  undoneBy: 'Undone By',
};

/**
 * The report's name for a row field.
 * @param {keyof CleanupLogRow} field - The row field
 * @return {string} Its label
 */
export const fieldLabel = (field: keyof CleanupLogRow): string =>
  FIELD_LABEL[field] ?? field;

// ImportedEntity's sources. Operator feed is what the Analytics panel calls the
// entities the operator feed creates.
const PROVENANCE_LABELS: Record<string, string> = {
  legacy_data_import: 'Legacy import',
  auto_provisioner: 'Auto-provisioned',
  operator_extraction: 'Operator feed',
};

export const ROLE_LABELS: Record<CleanupRole, string> = {
  deduplicated: 'Deduplicated',
  add_as_new: 'Added as New',
};

export const ACTION_OUTCOME_LABELS: Record<CleanupActionOutcome, string> = {
  dedup_only: 'Dedupe',
  alias_only: 'Alias',
  mixed: 'Dedupe + alias',
  add_as_new: 'Add as New',
};

export const ACTION_SOURCE_LABELS: Record<CleanupActionSource, string> = {
  mark_as_correct: 'Mark as Correct',
  auto_match: 'Auto-match',
  add_as_new: 'Add as New',
};

/**
 * A value through its label map, the raw value when unmapped, or null.
 * @param {Record<string, string>} labels - Labels by raw value
 * @param {string | null} value - The raw value
 * @return {string | null} The label
 */
export const labelFor = (
  labels: Record<string, string>,
  value: string | null
): string | null => (value ? (labels[value] ?? value) : null);

export const PROVENANCE_ITEMS = [
  { id: '', label: 'All provenance' },
  ...Object.entries(PROVENANCE_LABELS).map(([id, label]) => ({ id, label })),
];

/**
 * Provenance only describes Imported entities, so its filter applies while
 * Imported is in scope: no type picked, or Imported among them.
 * @param {EntityType[]} entityTypes - The picked entity types
 * @return {boolean} Whether provenance can filter
 */
export const provenanceApplies = (entityTypes: EntityType[]) =>
  entityTypes.length === 0 || entityTypes.includes('Imported');
